import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

function formatTimeParts(value) {
  if (!value) return { date: '-', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: String(value), time: '' };
  const [datePart, timePart] = date.toLocaleString().split(',').map((part) => part.trim());
  return {
    date: datePart || date.toLocaleDateString(),
    time: timePart || date.toLocaleTimeString(),
  };
}

function hasPayload(payload) {
  return payload && typeof payload === 'object' && Object.keys(payload).length > 0;
}

function summarize(event) {
  if (event.summary) return event.summary;
  const payload = event.payload || {};
  if (payload.reason) return payload.reason;
  if (payload.status) return String(payload.status).replaceAll('_', ' ');
  if (payload.result) return String(payload.result).replaceAll('_', ' ');
  if (payload.title) return payload.title;
  return '';
}

export default function AuditTrail({
  events = [],
  className = 'col-span-12 lg:col-span-5',
  previewHeight,
  expanded = false,
  onExpand,
  onCollapse,
}) {
  const [open, setOpen] = useState({});
  const [hasOverflow, setHasOverflow] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const hasPreview = Number.isFinite(previewHeight) && previewHeight > 0;
  const isCollapsedPreview = hasPreview && !expanded;

  const measureOverflow = useCallback(() => {
    if (!isCollapsedPreview || !containerRef.current || !contentRef.current) {
      setHasOverflow(false);
      return;
    }
    const nextHasOverflow = contentRef.current.scrollHeight > containerRef.current.clientHeight + 1;
    setHasOverflow(nextHasOverflow);
  }, [isCollapsedPreview]);

  useLayoutEffect(() => {
    measureOverflow();
  }, [events, open, previewHeight, expanded, measureOverflow]);

  useLayoutEffect(() => {
    if (!isCollapsedPreview) return undefined;
    window.addEventListener('resize', measureOverflow);
    return () => window.removeEventListener('resize', measureOverflow);
  }, [isCollapsedPreview, measureOverflow]);

  return (
    <section
      className={`${className} min-w-0 relative flex-col border border-zinc-200 rounded-sm p-6 ${isCollapsedPreview ? 'overflow-hidden' : ''}`}
      style={isCollapsedPreview ? { height: `${previewHeight}px` } : undefined}
      data-testid="audit-trail"
    >
      <h2 className="font-display text-lg font-semibold">Audit trail</h2>
      <div
        ref={containerRef}
        className={`relative mt-4 min-h-0 ${isCollapsedPreview ? 'flex-1 overflow-hidden' : ''}`}
      >
        <div ref={contentRef} className="divide-y divide-zinc-200/70">
          {events.length === 0 ? (
            <p className="text-sm text-zinc-500 py-6">No events yet.</p>
          ) : events.map((event, index) => {
            const key = event.id || `${event.type}-${event.created_at || event.at || index}`;
            const expanded = !!open[key];
            const payload = event.payload || {};
            const canExpand = hasPayload(payload);
            const summary = summarize(event);
            const time = formatTimeParts(event.created_at || event.at);

            return (
              <div key={key} data-testid={`event-${event.type}`}>
                <button
                  type="button"
                  onClick={() => canExpand && setOpen((current) => ({ ...current, [key]: !expanded }))}
                  className="w-full min-w-0 py-3 text-left hover:bg-zinc-50 disabled:hover:bg-transparent"
                  disabled={!canExpand}
                >
                  <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {canExpand ? (
                          expanded ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5}/> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" strokeWidth={1.5}/>
                        ) : <span className="w-3.5"/>}
                        <span className="min-w-0 break-words font-mono text-xs font-semibold">{event.type}</span>
                      </div>
                      {summary && <p className="mt-1 ml-5 text-xs text-zinc-600 break-words sm:truncate">{summary}</p>}
                    </div>
                    <span className="ml-5 min-w-0 break-words text-left font-mono text-[11px] leading-tight text-zinc-500 sm:ml-0 sm:shrink-0 sm:text-right">
                      <span className="block">{time.date}</span>
                      {time.time && <span className="mt-0.5 block text-zinc-400">{time.time}</span>}
                    </span>
                  </div>
                </button>
                {expanded && canExpand && (
                  <pre className="mb-3 max-w-full overflow-hidden rounded-sm border border-zinc-100 bg-zinc-50 p-2 font-mono text-[11px] whitespace-pre-wrap break-all">
{JSON.stringify(payload, null, 2)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
        {isCollapsedPreview && hasOverflow && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white via-white/95 to-transparent"/>
        )}
      </div>
      {(hasPreview && expanded || isCollapsedPreview && hasOverflow) && (
        <button
          type="button"
          onClick={expanded ? onCollapse : onExpand}
          className={`${isCollapsedPreview ? 'absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/95 border border-zinc-200 px-2.5 py-1.5' : 'mt-4 mx-auto'} w-fit rounded-sm text-sm font-medium text-zinc-900 hover:text-zinc-600`}
        >
          {expanded ? 'Show less' : 'See full audit trail'}
        </button>
      )}
    </section>
  );
}
